// React and Hooks
import { memo, useCallback, useEffect, useState } from "react";

// Project-Specific Types
import { TollDetailProps } from "./types";
import { TollDetailFormValues } from "services/api/calculations/toll/types";

// API Calls
import { getTollAPI } from "services/api/calculations/toll/api";

// UI Components
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";

// Cards and Specific Components
import TollEditCard from "../../../TollEditCard/TollEditCard";

// Project Constants & Helpers
import { CALCULATION_TOLL_EDIT_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { Errors } from "services/forms/validation/types";
import { initialTollDetailForm } from "services/api/calculations/toll/consts";
import { useTranslate } from "helpers";

const TollDetailCard: React.FC<TollDetailProps> = (props) => {
  const { tollId } = props;

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<TollDetailFormValues>(
    initialTollDetailForm
  );

  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  useEffect(() => {
    if (tollId) {
      fetchToll(tollId);
    }
  }, [tollId]);

  const fetchToll = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialTollDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getTollAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const handleEditSuccess = useCallback(() => {
    if (tollId) fetchToll(tollId);
    setShowEditModal(false);
  }, []);

  if (!tollId) return <></>;

  return (
    <>
      <div className="  flex justify-center ">
        <div className="rounded-sm bg-white border max-w-lg w-full">
          <div className="divide-y divide-slate-200">
            <div className="flex justify-between items-center">
              <p className="text-sm font-semibold text-slate-600 px-4 py-3 uppercase">
                {t("admin.calculation.toll.detail.title")}
              </p>
              <div className="flex items-center gap-2 px-3 my-3 ">
                <Button
                  size="small"
                  variant="primary"
                  children={t("admin.change")}
                  onClick={() => setShowEditModal(true)}
                  startIcon="/media/icons/plus.svg"
                />
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t("admin.calculation.toll.detail.fromccm")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.fromCcm} kw
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t("admin.calculation.toll.detail.toccm")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.toCcm} ccm
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t("admin.calculation.toll.detail.price")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.price} $
              </div>
            </div>
          </div>
        </div>
      </div>
      {showEditModal && (
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
          <ModalHeader
            title={CALCULATION_TOLL_EDIT_PAGE_TITLE.text}
            onHide={() => setShowEditModal(false)}
          />
          <ModalBody>
            <TollEditCard tollId={tollId} onSuccess={handleEditSuccess} />
          </ModalBody>
        </Modal>
      )}
      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(TollDetailCard);
